import { apiGet, apiPatch, apiPostForm } from './apiClient';
import type {
  MailAttachmentUrlDto,
  MailFolder,
  MailMailboxDto,
  MailMovableFolder,
  MailSendInput,
  MailSendResultDto,
  MailThreadDetailDto,
  MailThreadListDto,
  MailThreadSummaryDto,
} from './mailTypes';

/** Page size the thread list asks the server for. */
export const MAIL_PAGE_SIZE = 30;

const enc = encodeURIComponent;

export const listMailboxes = () => apiGet<MailMailboxDto[]>('/mail/mailboxes');

export const listThreads = (
  mailboxId: string,
  folder: MailFolder,
  opts: { cursor?: string; limit?: number } = {},
) => {
  const qs = new URLSearchParams({ folder, limit: String(opts.limit ?? MAIL_PAGE_SIZE) });
  if (opts.cursor) qs.set('cursor', opts.cursor);
  return apiGet<MailThreadListDto>(`/mail/mailboxes/${enc(mailboxId)}/threads?${qs.toString()}`);
};

/** The server marks the thread read as a side effect of this call. */
export const getThread = (threadId: string) =>
  apiGet<MailThreadDetailDto>(`/mail/threads/${enc(threadId)}`);

export const setThreadState = (threadId: string, state: { read?: boolean; starred?: boolean }) =>
  apiPatch<MailThreadSummaryDto>(`/mail/threads/${enc(threadId)}/state`, state);

export const moveThread = (threadId: string, folder: MailMovableFolder) =>
  apiPatch<MailThreadSummaryDto>(`/mail/threads/${enc(threadId)}/folder`, { folder });

export const getAttachmentUrl = (threadId: string, messageId: string, index: number) =>
  apiGet<MailAttachmentUrlDto>(
    `/mail/threads/${enc(threadId)}/messages/${enc(messageId)}/attachments/${index}/url`,
  );

/** Uploads can be 25 MB; allow more than the 30 s default. */
const SEND_TIMEOUT_MS = 120_000;

/** Compose / reply / forward: multipart with the JSON `payload` field plus one `attachments` part per file. */
export const sendMail = ({ payload, attachments = [] }: MailSendInput) => {
  const form = new FormData();
  form.append('payload', JSON.stringify(payload));
  for (const file of attachments) form.append('attachments', file, file.name);
  return apiPostForm<MailSendResultDto>('/mail/messages', form, { timeout: SEND_TIMEOUT_MS });
};
